import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchApi } from './fetchApi';

interface Kategorie {
  id: number;
  name: string;
  beschreibung: string | null;
  parent_id: number | null;
  parent_name: string | null;
}

interface FormDaten {
  name: string;
  beschreibung: string;
  parent_id: string;
}

const leerForm: FormDaten = { name: '', beschreibung: '', parent_id: '' };

export default function KategorieFormular() {
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  // Modal: Anlegen / Bearbeiten
  const [modalOffen, setModalOffen] = useState(false);
  const [bearbeitenId, setBearbeitenId] = useState<number | null>(null);
  const [form, setForm] = useState<FormDaten>(leerForm);
  const [modalFehler, setModalFehler] = useState<string | null>(null);

  // Bestätigungsdialog: Löschen
  const [loeschenId, setLoeschenId] = useState<number | null>(null);
  const [loeschenName, setLoeschenName] = useState('');

  async function ladeKategorien() {
    try {
      const res = await fetchApi('/api/kategorie');
      const data = await res.json();
      setKategorien(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fehler beim Laden der Kategorien:', err);
    }
  }

  useEffect(() => {
    ladeKategorien();
  }, []);

  function oeffneAnlegenModal() {
    setForm(leerForm);
    setBearbeitenId(null);
    setModalFehler(null);
    setModalOffen(true);
  }

  function oeffneBearbeitenModal(k: Kategorie) {
    setForm({
      name: k.name,
      beschreibung: k.beschreibung || '',
      parent_id: k.parent_id ? String(k.parent_id) : '',
    });
    setBearbeitenId(k.id);
    setModalFehler(null);
    setModalOffen(true);
  }

  function schliesseModal() {
    setModalOffen(false);
  }

  function handleTextChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleSelectChange(e: SelectChangeEvent) {
    setForm((prev) => ({ ...prev, parent_id: e.target.value }));
  }

  async function handleSpeichern() {
    setModalFehler(null);

    if (!form.name.trim()) {
      setModalFehler('Name ist erforderlich.');
      return;
    }

    try {
      const res = await fetchApi(
        bearbeitenId ? `/api/kategorie/${bearbeitenId}` : '/api/kategorie',
        {
          method: bearbeitenId ? 'PUT' : 'POST',
          body: JSON.stringify({
            name: form.name,
            beschreibung: form.beschreibung || null,
            parent_id: form.parent_id || null,
          }),
        }
      );
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setModalFehler(data?.error || 'Fehler beim Speichern.');
        return;
      }

      setErfolg(bearbeitenId ? 'Kategorie aktualisiert.' : 'Kategorie angelegt.');
      setFehler(null);
      setModalOffen(false);
      ladeKategorien();
    } catch (err: any) {
      setModalFehler(
        err?.message === '401'
          ? 'Session abgelaufen. Bitte neu einloggen.'
          : (err?.message || 'Unbekannter Fehler.')
      );
    }
  }

  function oeffneLoeschenDialog(k: Kategorie) {
    setLoeschenId(k.id);
    setLoeschenName(k.name);
    setFehler(null);
    setErfolg(null);
  }

  function schliesseLoeschenDialog() {
    setLoeschenId(null);
    setLoeschenName('');
  }

  async function handleLoeschen() {
    if (!loeschenId) return;

    try {
      const res = await fetchApi(`/api/kategorie/${loeschenId}`, { method: 'DELETE' });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setFehler(data?.error || 'Löschen nicht möglich.');
        schliesseLoeschenDialog();
        return;
      }

      setErfolg('Kategorie gelöscht.');
      schliesseLoeschenDialog();
      ladeKategorien();
    } catch (err: any) {
      setFehler(
        err?.message === '401'
          ? 'Session abgelaufen. Bitte neu einloggen.'
          : (err?.message || 'Fehler beim Löschen.')
      );
      schliesseLoeschenDialog();
    }
  }

  // Beim Bearbeiten: aktuelle Kategorie aus Parent-Optionen ausschließen
  const parentOptionen = kategorien.filter((k) => k.id !== bearbeitenId);

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Kategorien</Typography>
        <Button variant="contained" onClick={oeffneAnlegenModal}>
          + Kategorie anlegen
        </Button>
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}
      {erfolg && <Alert severity="success" sx={{ mb: 2 }}>{erfolg}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Übergeordnete Kategorie</TableCell>
            <TableCell>Beschreibung</TableCell>
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {kategorien.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">Keine Kategorien vorhanden.</TableCell>
            </TableRow>
          ) : (
            kategorien.map((k) => (
              <TableRow key={k.id} hover>
                <TableCell>{k.name}</TableCell>
                <TableCell>{k.parent_name || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {k.beschreibung || '—'}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => oeffneBearbeitenModal(k)} title="Bearbeiten">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => oeffneLoeschenDialog(k)} title="Löschen">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Modal: Anlegen / Bearbeiten */}
      <Dialog open={modalOffen} onClose={schliesseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{bearbeitenId ? 'Kategorie bearbeiten' : 'Kategorie anlegen'}</DialogTitle>
        <DialogContent>
          {modalFehler && <Alert severity="error" sx={{ mb: 2 }}>{modalFehler}</Alert>}
          <TextField
            label="Name"
            name="name"
            value={form.name}
            onChange={handleTextChange}
            fullWidth
            required
            autoFocus
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Übergeordnete Kategorie</InputLabel>
            <Select
              value={form.parent_id}
              label="Übergeordnete Kategorie"
              onChange={handleSelectChange}
            >
              <MenuItem value="">(Keine)</MenuItem>
              {parentOptionen.map((k) => (
                <MenuItem key={k.id} value={String(k.id)}>
                  {k.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Beschreibung"
            name="beschreibung"
            value={form.beschreibung}
            onChange={handleTextChange}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={schliesseModal}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSpeichern}>
            {bearbeitenId ? 'Aktualisieren' : 'Anlegen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Löschen bestätigen */}
      <Dialog open={loeschenId !== null} onClose={schliesseLoeschenDialog}>
        <DialogTitle>Kategorie löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll <strong>{loeschenName}</strong> wirklich gelöscht werden?
            Dies ist nur möglich, wenn keine Unterkategorien oder Artikel zugeordnet sind.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={schliesseLoeschenDialog}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleLoeschen}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
